import React, { Component } from 'react';
import TvImageUploader from './TvImageUploader';
import { navigateTo } from '../utils/navigation';
import { listTournaments, updateTournament, ESTADO_TORNEO } from '../utils/tournamentsApi';
import {
    TIPOS, tipoDe, nombreDeTipo, formatoDuracion, duracionDeLaVuelta,
    getScreens, getBlocks, createBlock, updateBlock, moveBlock, deleteBlock,
    createScreen, deleteScreen, normalizarPantalla,
    getScreenSettings, updateScreenSettings, uploadScreenBackground, clearScreenBackground,
    uploadBlockBackground, clearBlockBackground, uploadBlockImage, getEventsForTv,
} from '../utils/cartelera';

const config = process.env;

/**
 * Cartelera TV — qué pasa el televisor del local y en qué orden.
 *
 * La pantalla es una LISTA ORDENADA y nada más: el operador agrega bloques, los acomoda y les
 * pone duración. Todo lo demás (qué eventos hay, qué juegos son novedad, qué dice el menú) ya
 * está cargado en otra parte del backoffice y se resuelve solo del lado del televisor. Ése es
 * el punto de la feature: una cartelera que no hay que mantener.
 *
 * Las dos cosas que el operador necesita ver de un vistazo y que no son obvias:
 *
 *   - **Cuánto dura la vuelta.** Con 12 bloques de 15 segundos, una promo nueva aparece una vez
 *     cada tres minutos. Ese número decide si conviene agregar otro bloque o acortar los que
 *     están, y sin mostrarlo nadie lo calcula.
 *   - **Qué bloques NO se están viendo.** Un bloque vencido, apagado o sin imagen se saltea en
 *     el televisor. Si la pantalla del backoffice los dibujara igual que al resto, el operador
 *     vería una cartelera que no existe.
 */

const urlDelTelevisor = (screen) => {
    const base = (config.REACT_APP_STOREFRONT_URL || '').replace(/\/+$/, '');
    const ruta = `/tv${screen && screen !== 'principal' ? `?pantalla=${encodeURIComponent(screen)}` : ''}`;
    return base ? `${base}${ruta}` : ruta;
};

// La página de diagnóstico vive en el storefront, fuera del bundle: está pensada para cargar
// incluso en un navegador que no puede con la cartelera.
const urlDiagnostico = () => {
    const base = (config.REACT_APP_STOREFRONT_URL || '').replace(/\/+$/, '');
    return `${base}/tv-diagnostico.html`;
};

const hoy = () => new Date().toISOString().slice(0, 10);

const nombreDeTorneo = (t) => t.title || t.eventTitle || `Torneo #${t.id}`;

const torneoDe = (torneos, id) => torneos.find((t) => Number(t.id) === Number(id)) || null;

// El torneo que el televisor elige solo en un bloque sin torneo elegido. Mismo criterio que
// `resolverTorneo` en el TvController del backend: en juego o en mesa final, con la tabla
// publicada, y el más nuevo. Si cambia allá, cambia acá.
const torneoAutomatico = (torneos) => torneos
    .filter((t) => (t.status === 'running' || t.status === 'final') && t.public_token)
    .sort((a, b) => Number(b.id) - Number(a.id))[0] || null;

// ── Eventos ──
//
// Una hora de pared como la emite el backend ('2026-10-04T20:00:00-03:00') en la forma
// 'AAAA-MM-DD HH:mm:ss', para compararla como texto.
const pared = (valor) => String(valor || '').slice(0, 19).replace('T', ' ');

// "Ahora" en el local, en esa misma forma. `sv-SE` es el formato que da AAAA-MM-DD HH:mm:ss.
const ahoraEnElLocal = () => new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Montevideo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
}).format(new Date());

// Mismo criterio que `eventoTermino` en el TvController: con hora de fin, cuando termina; sin
// ella, al terminar el día en que empieza.
const eventoTermino = (e, ahora) => (e.ends_at
    ? pared(e.ends_at) < ahora
    : pared(e.starts_at).slice(0, 10) < ahora.slice(0, 10));

const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

// "sáb 4/10 · 20:00", leído de los dígitos de la hora de pared y no de un Date en la zona del
// navegador: el evento es a las 20:00 en el local, lo mire quien lo mire.
const fechaDeEvento = (startsAt) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(startsAt || ''));
    if (!m) return '';
    const dia = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay();
    // El año sólo si no es éste: "vie 1/1" de otro año se confunde con uno de dentro de tres meses.
    const anio = m[1] !== String(new Date().getFullYear()) ? `/${m[1].slice(2)}` : '';
    return `${DIAS_CORTOS[dia]} ${+m[3]}/${+m[2]}${anio} · ${m[4]}:${m[5]}`;
};

const eventoDe = (eventos, id) => eventos.find((e) => Number(e.id) === Number(id)) || null;

// Sólo mostrador (`counter_only`: un cumpleaños dado de alta con «+ Evento privado»). Es de un
// cliente, no del local, y el televisor no lo anuncia NUNCA: el backend lo saltea aunque esté
// elegido y rechaza guardarlo en un bloque (7400). La regla es la marca y no la visibilidad: la
// jornada del club también es privada y se anuncia.
export const esSoloMostrador = (e) => !!(e && Number(e.counter_only));

// El que el televisor elige solo. Mismo criterio que `resolverEvento` del backend en modo
// automático: público, publicado, sin terminar, el más cercano. Un sólo mostrador no entra
// aunque le hayan destildado «privado» (el listado público del backend lo filtra por la marca).
export const eventoAutomatico = (eventos, ahora) => eventos
    .filter((e) => e.status === 'published' && e.visibility === 'public' && !esSoloMostrador(e) && !eventoTermino(e, ahora))
    .sort((a, b) => (pared(a.starts_at) < pared(b.starts_at) ? -1 : 1))[0] || null;

// Los eventos que ofrece el selector del bloque "Un evento": lo que tiene sentido anunciar —lo
// que no terminó, no se canceló y no es sólo mostrador—, del más cercano al más lejano.
export const eventosParaAnunciar = (eventos, ahora) => eventos
    .filter((e) => e.status !== 'cancelled' && !esSoloMostrador(e) && !eventoTermino(e, ahora))
    .sort((a, b) => (pared(a.starts_at) < pared(b.starts_at) ? -1 : 1));

const ESTADO_EVENTO = {
    draft: { label: 'Borrador', badge: 'badge-secondary' },
    published: { label: 'Publicado', badge: 'badge-success' },
    closed: { label: 'Cerrado', badge: 'badge-secondary' },
    cancelled: { label: 'Cancelado', badge: 'badge-danger' },
};

// Por qué un bloque no se está viendo en el televisor, o null si sí se ve. El texto es el que
// se muestra: tiene que decir la causa, no "inactivo".
//
// `torneos` / `eventos` null = la lista todavía no llegó (o falló). Ahí no se opina sobre esos
// bloques: marcar oculto algo que se ve es la misma mentira que la contraria.
export const motivoOculto = (b, torneos, eventos) => {
    if (!b.isActive) return 'Apagado';
    if (b.endsOn && b.endsOn < hoy()) return `Venció el ${b.endsOn}`;
    if (b.startsOn && b.startsOn > hoy()) return `Empieza el ${b.startsOn}`;
    if (b.kind === 'image' && !b.hasImage) return 'Falta subir la imagen';
    // El televisor saltea en silencio un torneo sin publicar: sin esto, la cartelera del
    // backoffice lo mostraba como si se viera y no había forma de saber por qué no salía.
    if (b.kind === 'tournament' && torneos) {
        if (b.tournamentId) {
            const t = torneoDe(torneos, b.tournamentId);
            if (!t) return 'No encuentro el torneo';
            if (!t.public_token) return 'Torneo sin publicar';
        } else if (!torneoAutomatico(torneos)) {
            return 'Ningún torneo en juego ahora';
        }
    }
    if (b.kind === 'event' && eventos) {
        const ahora = ahoraEnElLocal();
        if (b.eventId) {
            const e = eventoDe(eventos, b.eventId);
            if (!e) return 'No encuentro el evento';
            if (esSoloMostrador(e)) return 'Evento sólo mostrador: no se anuncia';
            if (e.status !== 'published') return `Evento: ${(ESTADO_EVENTO[e.status] || { label: e.status }).label.toLowerCase()}`;
            if (eventoTermino(e, ahora)) return 'El evento ya terminó';
        } else if (!eventoAutomatico(eventos, ahora)) {
            return 'Ningún evento por delante';
        }
    }
    return null;
};

// Los torneos del selector, agrupados por lo que le importa al que arma la cartelera: el que
// se está jugando arriba, los terminados al fondo. Sin esto era una lista por fecha de alta
// donde el clasificatorio de hoy y una liga de hace meses se veían iguales.
const GRUPOS_DE_TORNEO = [
    { etiqueta: 'En juego', incluye: (t) => t.status === 'running' || t.status === 'final' },
    { etiqueta: 'Por empezar', incluye: (t) => t.status === 'setup' },
    { etiqueta: 'Terminados', incluye: (t) => t.status === 'finished' },
];

// Un estado que no está en ningún grupo (uno nuevo, mañana) va aparte en vez de desaparecer: un
// bloque con ese torneo elegido mostraría "El que se esté jugando ahora" en el selector.
const torneosDelGrupo = (torneos, grupo) => (grupo
    ? torneos.filter(grupo.incluye)
    : torneos.filter((t) => !GRUPOS_DE_TORNEO.some((g) => g.incluye(t))));

/**
 * "Guardando…" / "Guardado" al lado del botón del televisor.
 *
 * La pantalla guarda sola al salir de cada campo. Sin una señal, eso se lee como que no pasó
 * nada — y acá se nota el doble, porque el efecto del cambio se ve en OTRA pantalla que encima
 * tarda hasta un minuto en tomarlo. El cartel se apaga a los 3 segundos para no quedar como
 * un adorno permanente.
 */
class EstadoDeGuardado extends Component {
    constructor(props) {
        super(props);
        this.state = { visible: false };
    }

    componentDidUpdate(prev) {
        if (this.props.guardadoEn && this.props.guardadoEn !== prev.guardadoEn) {
            clearTimeout(this.t);
            this.setState({ visible: true });
            this.t = setTimeout(() => !this.desmontado && this.setState({ visible: false }), 3000);
        }
    }

    componentWillUnmount() {
        this.desmontado = true;
        clearTimeout(this.t);
    }

    render() {
        if (this.props.guardando) {
            return <span className="text-muted" style={{ fontSize: 12 }}>Guardando…</span>;
        }
        if (!this.state.visible) return null;
        return (
            <span className="text-success" style={{ fontSize: 12 }}>
                <i className="material-icons" style={{ fontSize: 15, verticalAlign: 'text-bottom' }}>check</i>
                {' '}Guardado
            </span>
        );
    }
}

class TvCarteleraComponent extends Component {
    constructor(props) {
        super(props);
        // `?pantalla=` en la URL abre esa pantalla: el atajo "Configurar en Cartelera TV" de un
        // torneo te trae directo a la pantalla donde quedó su bloque.
        const params = new URLSearchParams(window.location.search);
        const pedida = params.get('pantalla');
        // `?bloque=` lleva a ese bloque: el "Configurar" de un torneo abre SU bloque, no el
        // principio de una lista donde hay que buscarlo.
        this.bloquePedido = Number(params.get('bloque')) || null;
        // Número de la última carga pedida; ver `cargar`.
        this.cargas = 0;
        this.state = {
            screen: pedida || 'principal',
            screens: [pedida || 'principal'],
            blocks: [],
            // El fondo que comparten los bloques de esta pantalla.
            pantalla: null,
            // id del bloque cuyo panel de fondo propio está abierto. Uno a la vez: el fondo
            // por bloque es la excepción, y tenerlos todos desplegados llena la pantalla de
            // cajas para algo que casi nunca se usa.
            fondoAbierto: null,
            tournaments: [],
            // false hasta que llega la lista: sin ella no se puede decir si un bloque de torneo
            // se ve o no (ver `motivoOculto`).
            torneosCargados: false,
            // Los eventos, para el bloque "Un evento". Mismo trato que los torneos.
            eventos: [],
            eventosCargados: false,
            // id del bloque recién agregado o pedido por URL: se lleva a la vista y se marca un
            // momento, para que se vea DÓNDE quedó.
            resaltado: null,
            cargando: true,
            guardando: null,     // id del bloque que se está guardando
            error: null,
            confirmarBorrado: null,
            // Momento del último guardado exitoso. La pantalla guarda al salir de cada campo,
            // sin botón "Guardar", y sin una señal visible eso se lee como "no se guardó nada"
            // — más todavía cuando el efecto se ve en OTRA pantalla, que encima tarda hasta un
            // minuto en tomarlo.
            guardadoEn: null,
            // Alta de pantalla: null = el formulario está cerrado.
            nuevaPantalla: null,
            confirmarBorrarPantalla: false,
        };
    }

    async componentDidMount() {
        this.cargarTorneos();
        this.cargarEventos();
        await this.cargar();
        if (this.bloquePedido) this.enfocarBloque(this.bloquePedido);
    }

    componentWillUnmount() {
        this.desmontado = true;
        clearTimeout(this.temporizador);
        clearTimeout(this.temporizadorResaltado);
    }

    // Los torneos son para el selector del bloque de tabla. Si falla, el selector queda vacío y
    // el bloque sigue funcionando en modo automático: no es motivo para romper la pantalla.
    cargarTorneos = () => listTournaments()
        .then((data) => !this.desmontado && this.setState({ tournaments: (data && data.tournaments) || [], torneosCargados: true }))
        .catch(() => {});

    cargarEventos = () => getEventsForTv()
        .then((data) => !this.desmontado && this.setState({ eventos: (data && data.events) || [], eventosCargados: true }))
        .catch(() => {});

    cargar = async (screen = this.state.screen) => {
        // El "Cargando…" desmonta la lista, así que va SÓLO al entrar o al cambiar de pantalla.
        // En el refresco que sigue a cada guardado la lista queda montada: desmontarla achicaba
        // la página y el scroll saltaba arriba de todo cada vez que se tocaba un campo, y los
        // campos que se estaban escribiendo se perdían.
        const cambiaDePantalla = screen !== this.state.screen;
        this.setState(cambiaDePantalla ? { cargando: true, error: null } : { error: null });
        // Dos campos guardados seguidos disparan dos cargas. Si la primera contesta DESPUÉS que
        // la segunda, pisaría lo recién guardado con la foto de antes: gana la última pedida.
        const numero = ++this.cargas;
        try {
            const [{ screens }, { blocks }, pantalla] = await Promise.all([
                getScreens(), getBlocks(screen), getScreenSettings(screen),
            ]);
            if (this.desmontado || numero !== this.cargas) return;
            this.setState({ screens, blocks, pantalla, screen, cargando: false });
        } catch (error) {
            if (this.desmontado || numero !== this.cargas) return;
            this.setState({ cargando: false, error: error.message });
        }
    };

    // Lleva un bloque a la vista y lo marca un momento. Salto instantáneo y no `smooth`: el
    // scroll suave no avanza con la pestaña en segundo plano (y queda en 0), y la marca ya dice
    // adónde se fue.
    enfocarBloque = (id) => {
        this.setState({ resaltado: id }, () => {
            const el = document.getElementById(`tv-bloque-${id}`);
            if (el) el.scrollIntoView({ block: 'center' });
        });
        clearTimeout(this.temporizadorResaltado);
        this.temporizadorResaltado = setTimeout(() => !this.desmontado && this.setState({ resaltado: null }), 2500);
    };

    // Una operación que devuelve la lista entera (mover) o un bloque (editar). En los dos
    // casos se recarga desde el servidor: dos personas pueden estar tocando la cartelera y el
    // orden es global, así que reconstruirlo en el cliente es pedirle a la pantalla que
    // adivine.
    operar = async (id, fn) => {
        this.enVuelo = (this.enVuelo || 0) + 1;
        this.setState({ guardando: id, error: null });
        try {
            await fn();
            await this.cargar();
            if (!this.desmontado) this.setState({ guardadoEn: Date.now() });
        } catch (error) {
            if (this.desmontado) return;
            this.setState({ error: error.message });
        } finally {
            // Los campos no se bloquean mientras se guarda, así que puede haber dos guardados en
            // vuelo: el primero que termina no apaga el "Guardando…" del otro.
            this.enVuelo -= 1;
            if (!this.desmontado && this.enVuelo === 0) this.setState({ guardando: null });
        }
    };

    // Guardado con retardo, para los sliders.
    //
    // Un `onMouseUp` sobre el propio slider parece suficiente y NO lo es: si se arrastra y se
    // suelta el botón FUERA del control (que es lo más común al llevarlo hasta una punta), ese
    // evento nunca llega y el cambio no se guarda. Con `onChange` + retardo se guarda siempre,
    // y el retardo evita mandar un PATCH por cada píxel del arrastre.
    guardarConRetardo = (fn) => {
        clearTimeout(this.temporizador);
        this.temporizador = setTimeout(() => this.operar(null, fn), 400);
    };

    // El bloque nuevo va al FINAL de la lista y los botones están arriba: sin llevarlo a la
    // vista, el click no parecía hacer nada y el bloque quedaba abajo, fuera de pantalla.
    agregar = async (kind) => {
        let nuevo = null;
        await this.operar(null, async () => {
            nuevo = await createBlock({
                screen: this.state.screen,
                kind,
                durationSeconds: kind === 'image' ? 12 : 20,
            });
        });
        if (nuevo && nuevo.id && !this.desmontado) this.enfocarBloque(nuevo.id);
    };

    // Publicar la tabla de un torneo desde acá: el televisor saltea un torneo sin publicar, y
    // mandar al operador a la pantalla del torneo sólo para eso es un rodeo.
    publicarTorneo = (id) => this.operar(null, async () => {
        await updateTournament(id, { published: true });
        await this.cargarTorneos();
    });

    editar = (id, cambios) => this.operar(id, () => updateBlock(id, cambios));

    mover = (id, direction) => this.operar(id, () => moveBlock(id, direction));

    borrar = (id) => {
        this.setState({ confirmarBorrado: null });
        return this.operar(id, () => deleteBlock(id));
    };

    // El fondo de la pantalla y el de un bloque pasan por `operar`, que recarga todo: cambiar
    // el fondo de la pantalla cambia lo que se ve en CADA bloque que lo hereda, así que
    // actualizar sólo la fila tocada dejaría la pantalla mintiendo.
    fondoDePantalla = (blob) => uploadScreenBackground(this.state.screen, blob);

    quitarFondoDePantalla = () => this.operar(null, () => clearScreenBackground(this.state.screen));

    // El slider se mueve YA (estado local) y el guardado va con retardo: si esperara al
    // servidor, el control daría saltos hacia atrás mientras se arrastra.
    ajustarFondo = (campo, valor) => {
        this.setState((prev) => ({ pantalla: { ...prev.pantalla, [campo]: valor } }));
        this.guardarConRetardo(() => updateScreenSettings(this.state.screen, { [campo]: valor }));
    };

    quitarFondoDeBloque = (id) => this.operar(id, () => clearBlockBackground(id));

    crearPantalla = async () => {
        const nombre = normalizarPantalla(this.state.nuevaPantalla);
        if (!nombre) return;
        this.setState({ guardando: 'pantalla', error: null });
        try {
            await createScreen(nombre);
            this.setState({ nuevaPantalla: null });
            // Se cambia a la pantalla recién creada: el operador la creó para llenarla.
            await this.cargar(nombre);
            this.setState({ guardadoEn: Date.now() });
        } catch (error) {
            if (!this.desmontado) this.setState({ error: error.message });
        } finally {
            if (!this.desmontado) this.setState({ guardando: null });
        }
    };

    borrarPantalla = async () => {
        const pantalla = this.state.screen;
        this.setState({ confirmarBorrarPantalla: false, guardando: 'pantalla', error: null });
        try {
            await deleteScreen(pantalla);
            await this.cargar('principal');
            this.setState({ guardadoEn: Date.now() });
        } catch (error) {
            if (!this.desmontado) this.setState({ error: error.message });
        } finally {
            if (!this.desmontado) this.setState({ guardando: null });
        }
    };

    // --- render --------------------------------------------------------------------------

    renderNuevaPantalla() {
        const escrito = this.state.nuevaPantalla;
        const nombre = normalizarPantalla(escrito);
        const repetida = this.state.screens.includes(nombre);
        const puede = nombre && !repetida;

        return (
            <div className="border rounded p-3 mt-3" style={{ background: '#fafbfc' }}>
                <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>NOMBRE DE LA PANTALLA NUEVA</label>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                    <input
                        type="text" className="form-control form-control-sm" style={{ width: 240 }}
                        placeholder="Vidriera, Cafetería, Salón…" maxLength={40} autoFocus
                        value={escrito}
                        onChange={(e) => this.setState({ nuevaPantalla: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter' && puede) this.crearPantalla(); }}
                    />
                    <button className="btn btn-sm btn-primary" disabled={!puede} onClick={this.crearPantalla}>
                        Crear
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => this.setState({ nuevaPantalla: null })}>
                        Cancelar
                    </button>
                </div>

                {/* Lo que va a quedar en la URL, mientras escribe. El nombre termina siendo
                    parte de la dirección que se teclea en el televisor, así que conviene que
                    lo vea ANTES de crearla y no descubrirlo después. */}
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    {nombre
                        ? <>Se va a abrir en el televisor con <code>{urlDelTelevisor(nombre)}</code></>
                        : 'Escribí un nombre: letras, números y espacios.'}
                    {repetida && <span className="text-danger"> · ya existe una pantalla con ese nombre</span>}
                </div>
            </div>
        );
    }

    // El fondo de la pantalla: se sube UNA vez y lo usan todos los bloques.
    renderFondo() {
        const { pantalla, blocks } = this.state;
        if (!pantalla) return null;
        const heredan = blocks.filter((b) => b.kind !== 'image' && !b.hasOwnBackground).length;

        return (
            <div className="card card-small mb-3">
                <div className="card-body py-3">
                    <label className="text-muted d-block mb-2" style={{ fontSize: 11 }}>FONDO DE LA PANTALLA</label>
                    <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                        <div style={{
                            width: 54, height: 96, flex: '0 0 auto', borderRadius: 4,
                            background: pantalla.backgroundUrl
                                ? `#111 center/cover url(${JSON.stringify(pantalla.backgroundUrl)})`
                                : '#111',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {!pantalla.backgroundUrl && (
                                <i className="material-icons" style={{ fontSize: 24, color: '#555' }}>wallpaper</i>
                            )}
                        </div>

                        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                                {pantalla.backgroundUrl
                                    ? <>Lo usan <strong>{heredan}</strong> bloque{heredan === 1 ? '' : 's'}. Los bloques de tipo Imagen no lo llevan: su foto ya ocupa el televisor entero.</>
                                    : <>Sin fondo, los bloques de texto se ven sobre negro. Subí una imagen vertical y la usan todos los bloques de esta pantalla.</>}
                            </p>

                            <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                                <TvImageUploader
                                    imageUrl={pantalla.backgroundUrl}
                                    etiqueta="fondo"
                                    onSubir={this.fondoDePantalla}
                                    onSubida={() => this.cargar()}
                                />
                                {pantalla.backgroundUrl && (
                                    <button className="btn btn-sm btn-outline-secondary" onClick={this.quitarFondoDePantalla}>
                                        Quitar
                                    </button>
                                )}
                            </div>

                            {/* Los dos controles que deciden si el texto se lee sobre la foto.
                                Van acá y no escondidos en un "avanzado": sobre una foto clara y
                                sin ellos, el texto blanco desaparece a tres metros.
                                Son DOS porque hacen cosas distintas — oscurecer baja el
                                contraste general, desenfocar mata el detalle fino — y con una
                                sola perilla no se puede tener una foto nítida y oscura, ni una
                                clara y difusa. */}
                            {pantalla.backgroundUrl && (
                                <div className="mt-3 d-flex flex-wrap" style={{ gap: 24 }}>
                                    <div style={{ flex: '1 1 240px', maxWidth: 320 }}>
                                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
                                            OSCURECER — {pantalla.overlay}%
                                        </label>
                                        <input
                                            type="range" min="0" max="100" step="5" className="form-control-range"
                                            value={pantalla.overlay}
                                            onChange={(e) => this.ajustarFondo('overlay', Number(e.target.value))}
                                        />
                                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: 11 }}>
                                            <span>la foto tal cual</span>
                                            <span>negro</span>
                                        </div>
                                    </div>

                                    <div style={{ flex: '1 1 240px', maxWidth: 320 }}>
                                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
                                            DESENFOCAR — {pantalla.blur}%
                                        </label>
                                        <input
                                            type="range" min="0" max="100" step="5" className="form-control-range"
                                            value={pantalla.blur}
                                            onChange={(e) => this.ajustarFondo('blur', Number(e.target.value))}
                                        />
                                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: 11 }}>
                                            <span>nítida</span>
                                            <span>difusa</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderCabecera() {
        const { screen, screens, blocks, pantalla } = this.state;
        const activos = blocks.filter((b) => !motivoOculto(b, this.torneosSiCargaron(), this.eventosSiCargaron()));
        const vuelta = duracionDeLaVuelta(activos);
        const url = urlDelTelevisor(screen);

        return (
            <div className="card card-small mb-3">
                <div className="card-body py-3">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 20 }}>
                        {/* El selector va SIEMPRE, aunque haya una sola pantalla: es donde
                            está el botón para agregar la segunda, y escondiéndolo hasta que
                            existan dos no hay forma de llegar a crearla. */}
                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>PANTALLA</label>
                            <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                <select
                                    className="form-control form-control-sm"
                                    value={screen}
                                    onChange={(e) => this.cargar(e.target.value)}
                                    style={{ minWidth: 150 }}
                                >
                                    {screens.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button
                                    className="btn btn-sm btn-white" title="Agregar una pantalla"
                                    onClick={() => this.setState({ nuevaPantalla: '', confirmarBorrarPantalla: false })}
                                >
                                    <i className="material-icons" style={{ fontSize: 18 }}>add</i>
                                </button>
                                {/* `principal` no se puede borrar: es la que abre el televisor
                                    cuando la URL no trae parámetro. */}
                                {screen !== 'principal' && (
                                    <button
                                        className="btn btn-sm btn-white text-danger" title="Borrar esta pantalla"
                                        onClick={() => this.setState({ confirmarBorrarPantalla: true, nuevaPantalla: null })}
                                    >
                                        <i className="material-icons" style={{ fontSize: 18 }}>delete</i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Cómo está colgado el televisor. Va acá arriba, con la pantalla, y no
                            en el fondo: es una propiedad del MONTAJE, y es lo primero que hay
                            que acertar — con la opción equivocada no se lee nada. */}
                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>EL TELEVISOR ESTÁ</label>
                            <select
                                className="form-control form-control-sm"
                                style={{ minWidth: 190 }}
                                value={(pantalla && pantalla.orientation) || 'vertical'}
                                disabled={!pantalla || this.state.guardando !== null}
                                onChange={(e) => this.operar(null, () => updateScreenSettings(screen, { orientation: e.target.value }))}
                            >
                                {/* Lo que se elige es cómo va a quedar EL TELEVISOR, no qué
                                    hace la página. Son dos opciones de giro porque el sentido
                                    lo decide el soporte y por dónde salen los cables: con una
                                    sola, la mitad de los montajes queda cabeza abajo. Y no hay
                                    forma de adivinar cuál es — se prueba y se ve. */}
                                <option value="vertical">Parado (el televisor ya gira)</option>
                                <option value="horizontal_izq">Lo giro hacia la izquierda</option>
                                <option value="horizontal_der">Lo giro hacia la derecha</option>
                            </select>
                        </div>

                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>EN PANTALLA</label>
                            <div style={{ fontSize: 15 }}>
                                <strong>{activos.length}</strong> de {blocks.length} bloques
                            </div>
                        </div>

                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>DURA LA VUELTA</label>
                            {/* Es el mismo número que "cada cuánto se repite cada bloque", así
                                que se dice UNA vez y con la lectura que le sirve al operador:
                                lo que acaba de cargar reaparece dentro de tanto. */}
                            <div style={{ fontSize: 15 }}>
                                <strong>{formatoDuracion(vuelta)}</strong>
                                {vuelta > 0 && <span className="text-muted"> hasta que se repite</span>}
                            </div>
                        </div>

                        <div className="ml-auto d-flex align-items-center" style={{ gap: 12 }}>
                            <EstadoDeGuardado guardando={this.state.guardando !== null} guardadoEn={this.state.guardadoEn} />
                            <a className="btn btn-sm btn-outline-primary" href={url} target="_blank" rel="noopener noreferrer">
                                <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'text-bottom' }}>open_in_new</i>
                                {' '}Ver cómo queda
                            </a>
                        </div>
                    </div>

                    {this.state.nuevaPantalla !== null && this.renderNuevaPantalla()}

                    {this.state.confirmarBorrarPantalla && (
                        <div className="alert alert-warning py-2 px-3 mt-3 mb-0" style={{ fontSize: 13 }}>
                            ¿Borrar la pantalla <strong>{screen}</strong> con sus {blocks.length} bloque
                            {blocks.length === 1 ? '' : 's'} y sus imágenes? El televisor que tenga esa
                            URL abierta va a quedar sin nada que mostrar.
                            <button className="btn btn-sm btn-danger ml-2" onClick={this.borrarPantalla}>Borrar</button>
                            <button className="btn btn-sm btn-outline-secondary ml-1" onClick={() => this.setState({ confirmarBorrarPantalla: false })}>Cancelar</button>
                        </div>
                    )}

                    <div className="mt-3 pt-3 border-top text-muted" style={{ fontSize: 12 }}>
                        {/* Las dos preguntas que se hace cualquiera la primera vez: cuándo se
                            guarda (no hay botón) y por qué el televisor todavía muestra lo de
                            antes (tarda hasta un minuto). Contestarlas acá sale más barato que
                            contestarlas por WhatsApp. */}
                        <div>
                            <strong>Los cambios se guardan solos</strong> al salir de cada campo o al
                            soltar un control. No hay botón de guardar.
                        </div>
                        <div className="mt-1">
                            En el televisor, abrí <code>{url}</code> a pantalla completa (tocando la
                            pantalla entra). Se actualiza solo <strong>hasta un minuto</strong> después
                            de cada cambio: no hace falta tocar el televisor, pero tampoco es
                            instantáneo.
                        </div>
                        {/* El navegador de una smart TV puede ser MUY viejo, y si no soporta
                            módulos ES la cartelera le queda en blanco sin un solo mensaje. Esta
                            página lo dice en 5 segundos, y está escrita para cargar incluso ahí. */}
                        <div className="mt-1">
                            ¿Televisor nuevo? Abrí primero{' '}
                            <a href={urlDiagnostico()} target="_blank" rel="noopener noreferrer">
                                <code>{urlDiagnostico()}</code>
                            </a>{' '}
                            en ese televisor: dice si su navegador puede con la cartelera antes de que
                            te lleves la sorpresa.
                        </div>
                        {pantalla && pantalla.orientation !== 'vertical' && (
                            <div className="mt-1">
                                La cartelera se dibuja <strong>acostada</strong>: en la computadora se ve
                                de costado y es correcto — al girar el televisor queda derecha. Si
                                queda al revés, es la otra opción de giro. Para revisarla derecha
                                desde acá, agregale <code>&amp;orientacion=vertical</code> a la URL.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderAgregar() {
        return (
            <div className="card card-small mb-3">
                <div className="card-body py-3">
                    <label className="text-muted d-block mb-2" style={{ fontSize: 11 }}>AGREGAR UN BLOQUE</label>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                        {TIPOS.map((t) => (
                            <button
                                key={t.kind}
                                className="btn btn-sm btn-outline-secondary text-left"
                                onClick={() => this.agregar(t.kind)}
                                disabled={this.state.guardando !== null}
                                title={t.ayuda}
                                style={{ maxWidth: 260 }}
                            >
                                <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'text-bottom' }}>{t.icono}</i>
                                {' '}{t.nombre}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    torneosSiCargaron = () => (this.state.torneosCargados ? this.state.tournaments : null);

    eventosSiCargaron = () => (this.state.eventosCargados ? this.state.eventos : null);

    renderBloque(bloque, indice, total) {
        const tipo = tipoDe(bloque.kind) || {};
        const oculto = motivoOculto(bloque, this.torneosSiCargaron(), this.eventosSiCargaron());
        const ocupado = this.state.guardando === bloque.id;
        const resaltado = this.state.resaltado === bloque.id;

        return (
            <div
                key={bloque.id}
                id={`tv-bloque-${bloque.id}`}
                className="card card-small mb-2"
                // Un bloque que no se está viendo se dibuja apagado: si se viera igual que el
                // resto, la pantalla del backoffice mostraría una cartelera que no existe.
                style={{
                    opacity: oculto ? 0.62 : 1,
                    boxShadow: resaltado ? '0 0 0 3px rgba(0, 123, 255, 0.55)' : undefined,
                    transition: 'box-shadow 0.4s',
                }}
            >
                <div className="card-body py-3">
                    <div className="d-flex" style={{ gap: 14 }}>
                        {/* Miniatura con la forma del televisor, o el ícono del tipo. */}
                        <div style={{
                            width: 54, height: 96, flex: '0 0 auto', borderRadius: 4,
                            background: bloque.imageUrl ? `#111 center/cover url(${JSON.stringify(bloque.imageUrl)})` : '#eef1f4',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {/* `tipo.icono` y nada más: todos los tipos traen el suyo, y un
                                nombre de Material Icon que NO existe en la fuente vieja que
                                empaqueta este backoffice no cae en un ícono por defecto —
                                RENDERIZA LA PALABRA, y la miniatura queda diciendo
                                "crop_portrait". */}
                            {!bloque.imageUrl && tipo.icono && (
                                <i className="material-icons text-muted" style={{ fontSize: 26 }}>{tipo.icono}</i>
                            )}
                        </div>

                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <div className="d-flex align-items-start">
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <div style={{ fontSize: 15, fontWeight: 500 }}>
                                        <span className="text-muted mr-1">{indice + 1}.</span>
                                        {nombreDeTipo(bloque.kind)}
                                        {/* Con varios bloques de torneo, "Tabla de torneo" a secas no dice
                                            cuál es cuál: el nombre sólo se veía abriendo el desplegable. */}
                                        {bloque.kind === 'tournament' ? (() => {
                                            const t = bloque.tournamentId ? torneoDe(this.state.tournaments, bloque.tournamentId) : null;
                                            const nombre = bloque.tournamentId
                                                ? (t ? nombreDeTorneo(t) : `Torneo #${bloque.tournamentId}`)
                                                : 'el que se esté jugando';
                                            return <span className="text-muted" style={{ fontWeight: 400 }}> · {nombre}</span>;
                                        })() : null}
                                        {bloque.kind === 'event' ? (() => {
                                            const e = bloque.eventId ? eventoDe(this.state.eventos, bloque.eventId) : null;
                                            const nombre = bloque.eventId
                                                ? (e ? e.title : `Evento #${bloque.eventId}`)
                                                : 'el próximo';
                                            return <span className="text-muted" style={{ fontWeight: 400 }}> · {nombre}</span>;
                                        })() : null}
                                        {oculto && (
                                            <span className="badge badge-warning ml-2" style={{ fontWeight: 400 }}>{oculto}</span>
                                        )}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 12 }}>{tipo.ayuda}</div>
                                </div>

                                <div className="ml-2" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                                    <button
                                        className="btn btn-sm btn-white" title="Subir"
                                        disabled={ocupado || indice === 0}
                                        onClick={() => this.mover(bloque.id, -1)}
                                    >
                                        <i className="material-icons" style={{ fontSize: 18 }}>arrow_upward</i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-white" title="Bajar"
                                        disabled={ocupado || indice === total - 1}
                                        onClick={() => this.mover(bloque.id, 1)}
                                    >
                                        <i className="material-icons" style={{ fontSize: 18 }}>arrow_downward</i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-white text-danger" title="Quitar de la cartelera"
                                        disabled={ocupado}
                                        onClick={() => this.setState({ confirmarBorrado: bloque.id })}
                                    >
                                        <i className="material-icons" style={{ fontSize: 18 }}>delete</i>
                                    </button>
                                </div>
                            </div>

                            {this.renderControles(bloque, ocupado)}

                            {this.state.confirmarBorrado === bloque.id && (
                                <div className="alert alert-warning py-2 px-3 mt-2 mb-0" style={{ fontSize: 13 }}>
                                    ¿Quitar este bloque de la cartelera?
                                    {bloque.hasImage && ' La imagen se borra.'}
                                    <button className="btn btn-sm btn-danger ml-2" onClick={() => this.borrar(bloque.id)}>Quitar</button>
                                    <button className="btn btn-sm btn-outline-secondary ml-1" onClick={() => this.setState({ confirmarBorrado: null })}>Cancelar</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFondoDelBloque(bloque, ocupado) {
        const abierto = this.state.fondoAbierto === bloque.id;
        const propio = bloque.hasOwnBackground;

        return (
            <div className="mt-2">
                <button
                    className="btn btn-sm btn-link px-0 text-muted"
                    style={{ fontSize: 12, textDecoration: 'none' }}
                    onClick={() => this.setState({ fondoAbierto: abierto ? null : bloque.id })}
                >
                    <i className="material-icons" style={{ fontSize: 15, verticalAlign: 'text-bottom' }}>
                        {abierto ? 'expand_less' : 'expand_more'}
                    </i>
                    {' '}Fondo: {propio ? 'propio de este bloque' : 'el de la pantalla'}
                </button>

                {abierto && (
                    <div className="border rounded p-3" style={{ background: '#fafbfc' }}>
                        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
                            <div style={{
                                width: 42, height: 75, flex: '0 0 auto', borderRadius: 4,
                                background: bloque.backgroundUrl
                                    ? `#111 center/cover url(${JSON.stringify(bloque.backgroundUrl)})`
                                    : '#eef1f4',
                            }} />
                            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                                <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                                    {propio
                                        ? 'Este bloque usa su propio fondo en vez del de la pantalla.'
                                        : 'Usa el fondo de la pantalla. Subí uno acá sólo si querés que este bloque sea distinto.'}
                                </p>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                                    <TvImageUploader
                                        imageUrl={bloque.backgroundUrl}
                                        etiqueta="fondo propio"
                                        onSubir={(blob) => uploadBlockBackground(bloque.id, blob)}
                                        onSubida={() => this.cargar()}
                                    />
                                    {propio && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary" disabled={ocupado}
                                            onClick={() => this.quitarFondoDeBloque(bloque.id)}
                                        >
                                            Volver al de la pantalla
                                        </button>
                                    )}
                                </div>

                                {/* El velo propio: sólo tiene sentido si este bloque tiene algo
                                    de fondo (propio o heredado). Vacío = hereda el de la
                                    pantalla, y así se puede deshacer sin borrar el bloque. */}
                                <div className="mt-3 d-flex" style={{ gap: 16 }}>
                                    {[
                                        { campo: 'overlay', etiqueta: 'OSCURECER' },
                                        { campo: 'blur', etiqueta: 'DESENFOCAR' },
                                    ].map(({ campo, etiqueta }) => (
                                        <div key={campo}>
                                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
                                                {etiqueta} (vacío = como la pantalla)
                                            </label>
                                            <input
                                                type="number" min="0" max="100" className="form-control form-control-sm"
                                                style={{ width: 120 }} placeholder="hereda"
                                                defaultValue={bloque[campo] == null ? '' : bloque[campo]}
                                                key={`${campo}-${bloque.id}-${bloque[campo]}`}
                                                onBlur={(e) => {
                                                    const v = e.target.value === '' ? '' : Number(e.target.value);
                                                    if ((v === '' ? null : v) !== bloque[campo]) this.editar(bloque.id, { [campo]: v });
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Qué va a mostrar el televisor con este bloque de torneo, dicho al lado del selector. Es lo
    // que no se ve desde acá: un torneo sin publicar el televisor lo saltea en silencio, y en
    // modo automático no hay forma de saber cuál está agarrando.
    renderEstadoDelTorneo(bloque) {
        const { tournaments, torneosCargados } = this.state;
        if (!torneosCargados) return null;
        const estilo = { fontSize: 12, marginTop: 6 };

        if (!bloque.tournamentId) {
            const auto = torneoAutomatico(tournaments);
            return (
                <div className="text-muted" style={estilo}>
                    {auto
                        ? <>Ahora muestra <strong>{nombreDeTorneo(auto)}</strong>. Cuando termine, pasa solo al próximo torneo publicado que se juegue.</>
                        : <>Ahora no hay ningún torneo publicado en juego, así que este bloque no aparece. Sale solo el día que se juegue uno.</>}
                </div>
            );
        }

        const t = torneoDe(tournaments, bloque.tournamentId);
        if (!t) {
            return (
                <div className="text-danger" style={estilo}>
                    No encuentro el torneo #{bloque.tournamentId}: el televisor saltea este bloque. Elegí otro o dejalo en automático.
                </div>
            );
        }
        const estado = ESTADO_TORNEO[t.status] || { label: t.status, badge: 'badge-secondary' };
        return (
            <div className="d-flex align-items-center flex-wrap" style={{ ...estilo, gap: 8 }}>
                <span className={`badge ${estado.badge}`} style={{ fontWeight: 400 }}>{estado.label}</span>
                {!t.public_token ? (
                    <>
                        <span className="text-danger">La tabla de este torneo no está publicada: el televisor no lo muestra.</span>
                        <button
                            className="btn btn-sm btn-outline-primary py-0"
                            disabled={this.state.guardando !== null}
                            onClick={() => this.publicarTorneo(t.id)}
                        >
                            Publicar la tabla
                        </button>
                    </>
                ) : t.status === 'finished' ? (
                    <span className="text-muted">Muestra la clasificación final. Poné una fecha en HASTA para que salga sola de la cartelera.</span>
                ) : t.status === 'setup' ? (
                    <span className="text-muted">Todavía no empezó: la tabla se va llenando a medida que se cargan las mesas.</span>
                ) : (
                    <span className="text-muted">Se ve con la tabla en vivo.</span>
                )}
                <button className="btn btn-sm btn-link p-0" style={{ fontSize: 12 }} onClick={() => navigateTo(`/tournament?id=${t.id}`)}>
                    Abrir el torneo
                </button>
            </div>
        );
    }

    // El selector del bloque "Un evento". Ofrece lo que tiene sentido anunciar —lo que no terminó,
    // no se canceló y no es sólo mostrador, del más cercano al más lejano— y además el elegido
    // aunque ya no califique: si no estuviera entre las opciones, el select mostraría "El próximo
    // evento" y mentiría.
    renderSelectorDeEvento(bloque) {
        const ahora = ahoraEnElLocal();
        const proximos = eventosParaAnunciar(this.state.eventos, ahora);
        const elegido = bloque.eventId ? eventoDe(this.state.eventos, bloque.eventId) : null;
        const opcion = (e) => (
            <option key={e.id} value={e.id}>
                {fechaDeEvento(e.starts_at)} — {e.title}
                {esSoloMostrador(e) ? ' (sólo mostrador)' : (e.visibility === 'private' ? ' (privado)' : '')}
                {e.status !== 'published' ? ` (${(ESTADO_EVENTO[e.status] || { label: e.status }).label.toLowerCase()})` : ''}
            </option>
        );
        return (
            <div>
                <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>QUÉ EVENTO</label>
                <select
                    className="form-control form-control-sm" style={{ minWidth: 280, maxWidth: 420 }}
                    value={bloque.eventId || ''}
                    onChange={(e) => this.editar(bloque.id, { eventId: e.target.value })}
                >
                    <option value="">El próximo evento</option>
                    {proximos.map(opcion)}
                    {elegido && !proximos.includes(elegido) ? opcion(elegido) : null}
                </select>
            </div>
        );
    }

    // Qué va a mostrar el televisor con este bloque, al lado del selector: si el evento es un
    // borrador, se canceló o ya pasó, el televisor lo saltea en silencio.
    renderEstadoDelEvento(bloque) {
        const { eventos, eventosCargados } = this.state;
        if (!eventosCargados) return null;
        const estilo = { fontSize: 12, marginTop: 6 };
        const ahora = ahoraEnElLocal();

        if (!bloque.eventId) {
            const auto = eventoAutomatico(eventos, ahora);
            return (
                <div className="text-muted" style={estilo}>
                    {auto
                        ? <>Ahora muestra <strong>{auto.title}</strong> ({fechaDeEvento(auto.starts_at)}). Cuando termine, pasa solo al siguiente.</>
                        : <>No hay eventos públicos por delante, así que este bloque no aparece.</>}
                </div>
            );
        }

        const e = eventoDe(eventos, bloque.eventId);
        if (!e) {
            return (
                <div className="text-danger" style={estilo}>
                    No encuentro el evento #{bloque.eventId}: el televisor saltea este bloque. Elegí otro o dejalo en automático.
                </div>
            );
        }
        // Un sólo mostrador que quedó elegido (de antes de la marca, o por otra vía): el
        // televisor lo saltea siempre, esté como esté.
        if (esSoloMostrador(e)) {
            return (
                <div className="text-danger" style={estilo}>
                    <strong>{e.title}</strong> es sólo mostrador: el televisor no lo anuncia y saltea este bloque. Elegí otro o dejalo en automático.
                </div>
            );
        }
        const estado = ESTADO_EVENTO[e.status] || { label: e.status, badge: 'badge-secondary' };
        const termino = eventoTermino(e, ahora);
        return (
            <div className="d-flex align-items-center flex-wrap" style={{ ...estilo, gap: 8 }}>
                <span className={`badge ${termino ? 'badge-secondary' : estado.badge}`} style={{ fontWeight: 400 }}>
                    {termino ? 'Terminado' : estado.label}
                </span>
                {termino ? (
                    <span className="text-danger">Ya terminó: el bloque no aparece. Elegí otro o dejalo en automático.</span>
                ) : e.status === 'draft' ? (
                    <span className="text-danger">Es un borrador: el televisor lo muestra cuando lo publiques.</span>
                ) : e.status !== 'published' ? (
                    <span className="text-danger">No está publicado: el televisor no lo muestra.</span>
                ) : (
                    <span className="text-muted">
                        Se ve con póster, entradas, el cupo y un QR para sacar la entrada. Deja de aparecer solo cuando termina.
                    </span>
                )}
                <button className="btn btn-sm btn-link p-0" style={{ fontSize: 12 }} onClick={() => navigateTo(`/editEvent?id=${e.id}`)}>
                    Abrir el evento
                </button>
            </div>
        );
    }

    // Los controles de un bloque, en una sola fila. Cada control está al lado del dato que
    // toca, y sin botón "guardar": el cambio se manda al salir del campo (los selects y
    // checkboxes, al tocarlos).
    renderControles(bloque, ocupado) {
        const tipo = tipoDe(bloque.kind) || {};

        return (
            <div className="mt-2">
                <div className="d-flex flex-wrap align-items-end" style={{ gap: 14 }}>
                    <div>
                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>DURA</label>
                        <div className="input-group input-group-sm" style={{ width: 110 }}>
                            <input
                                type="number" min="3" max="300" className="form-control"
                                defaultValue={bloque.durationSeconds}
                                key={`dur-${bloque.id}-${bloque.durationSeconds}`}
                                onBlur={(e) => {
                                    const v = Number(e.target.value);
                                    if (v !== bloque.durationSeconds) this.editar(bloque.id, { durationSeconds: v });
                                }}
                            />
                            <div className="input-group-append"><span className="input-group-text">s</span></div>
                        </div>
                    </div>

                    {/* Vigencia: es lo que hace que una promo se apague sola el día que
                        corresponde, en vez de quedar en el televisor hasta que alguien se
                        acuerde de sacarla. */}
                    <div>
                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>DESDE (opcional)</label>
                        <input
                            type="date" className="form-control form-control-sm" style={{ width: 150 }}
                            defaultValue={bloque.startsOn || ''}
                            key={`ini-${bloque.id}-${bloque.startsOn}`}
                            onBlur={(e) => {
                                if ((e.target.value || null) !== bloque.startsOn) this.editar(bloque.id, { startsOn: e.target.value });
                            }}
                        />
                    </div>
                    <div>
                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>HASTA (opcional)</label>
                        <input
                            type="date" className="form-control form-control-sm" style={{ width: 150 }}
                            defaultValue={bloque.endsOn || ''}
                            key={`fin-${bloque.id}-${bloque.endsOn}`}
                            onBlur={(e) => {
                                if ((e.target.value || null) !== bloque.endsOn) this.editar(bloque.id, { endsOn: e.target.value });
                            }}
                        />
                    </div>

                    {tipo.llevaItems && (
                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
                                {tipo.etiquetaItems || 'CUÁNTOS MUESTRA'}
                            </label>
                            <input
                                type="number" min="0" max="60" className="form-control form-control-sm" style={{ width: 90 }}
                                placeholder={String(tipo.itemsDefault || 'auto')}
                                // `itemLimit` puede ser 0 ("todos") y 0 es falsy: con `|| ''` el
                                // campo se vaciaría solo y volvería al default en cada recarga.
                                defaultValue={bloque.itemLimit == null ? '' : bloque.itemLimit}
                                key={`lim-${bloque.id}-${bloque.itemLimit}`}
                                onBlur={(e) => {
                                    const v = e.target.value === '' ? '' : Number(e.target.value);
                                    if ((v === '' ? null : v) !== bloque.itemLimit) this.editar(bloque.id, { itemLimit: v });
                                }}
                            />
                            {/* Las dos cosas que no se adivinan mirando un campo numérico. */}
                            <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                vacío = {tipo.itemsDefault} · 0 = todos
                            </div>
                        </div>
                    )}

                    {tipo.llevaTorneo && (
                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>QUÉ TORNEO</label>
                            <select
                                className="form-control form-control-sm" style={{ minWidth: 240 }}
                                value={bloque.tournamentId || ''}
                                onChange={(e) => this.editar(bloque.id, { tournamentId: e.target.value })}
                            >
                                <option value="">El que se esté jugando ahora</option>
                                {[...GRUPOS_DE_TORNEO, null].map((g) => {
                                    const delGrupo = torneosDelGrupo(this.state.tournaments, g);
                                    if (!delGrupo.length) return null;
                                    return (
                                        <optgroup key={g ? g.etiqueta : 'otros'} label={g ? g.etiqueta : 'Otros'}>
                                            {delGrupo.map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {nombreDeTorneo(t)}
                                                    {t.game_name ? ` — ${t.game_name}` : ''}
                                                    {t.public_token ? '' : ' (sin publicar)'}
                                                </option>
                                            ))}
                                        </optgroup>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    {tipo.llevaEvento && this.renderSelectorDeEvento(bloque)}

                    {bloque.kind !== 'image' && (
                        <div>
                            <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>
                                {bloque.kind === 'event' ? 'ARRIBA DEL TÍTULO' : 'TÍTULO EN PANTALLA'}
                            </label>
                            <input
                                type="text" className="form-control form-control-sm" style={{ width: 220 }}
                                placeholder="(el de fábrica)" maxLength={160}
                                defaultValue={bloque.title || ''}
                                key={`tit-${bloque.id}-${bloque.title}`}
                                onBlur={(e) => {
                                    if ((e.target.value || null) !== bloque.title) this.editar(bloque.id, { title: e.target.value });
                                }}
                            />
                        </div>
                    )}

                    <div>
                        <label className="text-muted d-block mb-1" style={{ fontSize: 11 }}>ESTADO</label>
                        <button
                            className={`btn btn-sm ${bloque.isActive ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                            onClick={() => this.editar(bloque.id, { isActive: !bloque.isActive })}
                        >
                            <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'text-bottom' }}>
                                {bloque.isActive ? 'visibility' : 'visibility_off'}
                            </i>
                            {' '}{bloque.isActive ? 'Se muestra' : 'Apagado'}
                        </button>
                    </div>
                </div>

                {tipo.llevaTorneo && this.renderEstadoDelTorneo(bloque)}
                {tipo.llevaEvento && this.renderEstadoDelEvento(bloque)}

                {tipo.llevaArchivo && (
                    <div className="mt-2">
                        <TvImageUploader
                            imageUrl={bloque.imageUrl}
                            onSubir={(blob) => uploadBlockImage(bloque.id, blob)}
                            onSubida={() => this.cargar()}
                        />
                    </div>
                )}

                {/* Fondo PROPIO del bloque. Plegado por defecto: es la excepción —lo normal es
                    heredar el de la pantalla— y desplegado en los seis bloques llenaría la
                    pantalla de cajas para algo que casi nunca se toca. */}
                {bloque.kind !== 'image' && this.renderFondoDelBloque(bloque, ocupado)}

                <div className="mt-2">
                    <input
                        type="text" className="form-control form-control-sm"
                        placeholder="Nota para vos (no se muestra en el televisor)" maxLength={255}
                        defaultValue={bloque.note || ''}
                        key={`nota-${bloque.id}-${bloque.note}`}
                        onBlur={(e) => {
                            if ((e.target.value || null) !== bloque.note) this.editar(bloque.id, { note: e.target.value });
                        }}
                    />
                </div>
            </div>
        );
    }

    render() {
        const { blocks, cargando, error } = this.state;

        return (
            <div>
                {error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {this.renderCabecera()}
                {this.renderFondo()}
                {this.renderAgregar()}

                {cargando && <div className="text-muted">Cargando…</div>}

                {!cargando && blocks.length === 0 && (
                    <div className="card card-small">
                        <div className="card-body text-center text-muted py-5">
                            <i className="material-icons" style={{ fontSize: 40, opacity: 0.4 }}>tv</i>
                            <p className="mt-2 mb-0">
                                La cartelera está vacía: el televisor no muestra nada.<br />
                                Agregá un bloque con los botones de arriba.
                            </p>
                        </div>
                    </div>
                )}

                {!cargando && blocks.map((b, i) => this.renderBloque(b, i, blocks.length))}
            </div>
        );
    }
}

export default TvCarteleraComponent;
